import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

export default function ForgotPasswordPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
      setSent(true);
      toast.success(t("fp.sent") || "E-Mail gesendet, falls das Konto existiert.");
    } catch (_e) {
      setSent(true); // always show success to avoid enumeration
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-neon flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="fp-back">
            <ArrowLeft size={14} /> {t("fp.back") || "Zurück zum Login"}
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="mb-8 flex justify-center">
          <Logo to="/" />
        </div>
        {sent ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center" data-testid="fp-success">
            <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={40} />
            <h2 className="font-display text-2xl font-black">{t("fp.check_inbox") || "Check deine Inbox"}</h2>
            <p className="mt-2 text-sm text-zinc-400">
              {t("fp.check_inbox_sub") ||
                "Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Reset-Link gesendet. Der Link ist 1 Stunde gültig."}
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="mt-5 h-11 w-full rounded-xl bg-neon text-black font-bold hover:bg-neon-hover"
              data-testid="fp-back-login"
            >
              {t("fp.back_login") || "Zurück zum Login"}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight">
              {t("fp.h") || "Passwort vergessen?"}
            </h1>
            <p className="mt-2 text-sm text-zinc-400">
              {t("fp.sub") || "Wir senden dir einen sicheren Reset-Link per E-Mail."}
            </p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="fp-form">
              <div>
                <Label htmlFor="fp-email" className="text-zinc-300">{t("login.email")}</Label>
                <div className="mt-1.5 relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-10 rounded-xl bg-zinc-900 border-white/10 focus-visible:border-neon focus-visible:ring-neon"
                    data-testid="fp-email-input"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="h-12 rounded-xl bg-neon text-black font-bold hover:bg-neon-hover shadow-neon"
                data-testid="fp-submit-btn"
              >
                {loading ? (t("fp.sending") || "Wird gesendet…") : (t("fp.send") || "Reset-Link senden")}
              </Button>
            </form>
          </>
        )}
        <Footer variant="compact" />
      </div>
    </div>
  );
}
